import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

// ── Aadhaar Card Renderer ────────────────────────────────────────────────────
// Parses Aadhaar XML (DigiLocker KYC certificate format) and renders a card
// that mirrors the physical Aadhaar card layout.
const parseAadhaarXml = (xmlText) => {
    try {
        const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
        if (doc.querySelector('parsererror')) return null;
        const poi = doc.querySelector('Poi');
        const poa = doc.querySelector('Poa');
        const uid = doc.querySelector('UidData')?.getAttribute('uid') || '';
        const pht = doc.querySelector('Pht')?.textContent?.trim() || '';
        if (!poi) return null;
        const addr = [
            poa?.getAttribute('co'),
            poa?.getAttribute('house'),
            poa?.getAttribute('street'),
            poa?.getAttribute('loc'),
            poa?.getAttribute('vtc'),
            poa?.getAttribute('dist'),
            poa?.getAttribute('state'),
            poa?.getAttribute('pc'),
        ].filter(Boolean).join(', ');
        return {
            name: poi.getAttribute('name') || '',
            dob: poi.getAttribute('dob') || '',
            gender: poi.getAttribute('gender') || '',
            uid,
            address: addr,
            photo: pht,
        };
    } catch { return null; }
};

const AadhaarCard = ({ data }) => {
    const genderLabel = data.gender === 'M' ? 'Male' : data.gender === 'F' ? 'Female' : data.gender;
    const uidFormatted = data.uid.replace(/(\w{4})(?=\w)/g, '$1 ');

    return (
        <div style={{ width: '100%', maxWidth: '420px', margin: '0 auto', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Arial, sans-serif' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 60%, #1976d2 100%)', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', marginBottom: '3px' }}>भारत सरकार | Government of India</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#ff8f00', fontSize: '22px', fontWeight: 900, letterSpacing: '1px' }}>आधार</span>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600 }}>AADHAAR</span>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '8px', letterSpacing: '0.5px', lineHeight: 1.6 }}>
                        UNIQUE IDENTIFICATION<br />AUTHORITY OF INDIA
                    </div>
                </div>
            </div>

            {/* Body */}
            <div style={{ background: '#fafafa', padding: '14px 16px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                {/* Photo */}
                <div style={{ flexShrink: 0 }}>
                    {data.photo ? (
                        <img
                            src={`data:image/jpeg;base64,${data.photo}`}
                            alt="Aadhaar Photo"
                            style={{ width: '80px', height: '96px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #1565c0' }}
                        />
                    ) : (
                        <div style={{ width: '80px', height: '96px', background: '#e3f2fd', borderRadius: '6px', border: '2px solid #1565c0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="pi pi-user" style={{ fontSize: '28px', color: '#1565c0' }} />
                        </div>
                    )}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: '#0d47a1', marginBottom: '6px', wordBreak: 'break-word' }}>
                        {data.name}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '6px' }}>
                        <div>
                            <div style={{ fontSize: '9px', color: '#777', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date of Birth</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#222' }}>{data.dob}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '9px', color: '#777', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gender</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#222' }}>{genderLabel}</div>
                        </div>
                    </div>
                    {data.address && (
                        <div>
                            <div style={{ fontSize: '9px', color: '#777', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Address</div>
                            <div style={{ fontSize: '10px', color: '#444', lineHeight: '1.5', wordBreak: 'break-word' }}>{data.address}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* UID Footer */}
            <div style={{ background: 'linear-gradient(90deg, #0d47a1, #1565c0)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <i className="pi pi-shield" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }} />
                <span style={{ color: 'white', fontSize: '20px', fontWeight: 800, letterSpacing: '5px', fontFamily: 'monospace' }}>
                    {uidFormatted}
                </span>
            </div>
        </div>
    );
};

const FLOW_STEPS = [
    { label: 'Initialize' },
    { label: 'Authorize' },
    { label: 'Status' },
    { label: 'Documents' },
    { label: 'Download' },
];

const DigilockerFlowComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Shared session token passed between steps
    const [clientId, setClientId] = useState('');
    const [digilockerUrl, setDigilockerUrl] = useState('');

    // Per-step API results
    const [statusResult, setStatusResult] = useState(null);
    const [documentsResult, setDocumentsResult] = useState(null);
    const [fileId, setFileId] = useState('');
    const [downloadResult, setDownloadResult] = useState(null);
    const [aadhaarData, setAadhaarData] = useState(null);
    const [loadingAadhaar, setLoadingAadhaar] = useState(false);

    // Step 0 form
    const [form, setForm] = useState({
        full_name: '', mobile_number: '', user_email: '',
        expiry_minutes: '', redirect_url: '', consent: false,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${serviceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [serviceId]);

    // When the download URL is available, silently fetch and parse the Aadhaar XML
    // to render a visual card instead of showing raw XML.
    useEffect(() => {
        if (!downloadResult) return;
        const d = downloadResult?.data?.data || downloadResult?.data || {};
        const url = d.download_url || d.url || d.file_url;
        if (!url) return;
        setLoadingAadhaar(true);
        fetch(url)
            .then((r) => r.text())
            .then((xml) => {
                const parsed = parseAadhaarXml(xml);
                if (parsed) setAadhaarData(parsed);
            })
            .catch(() => {/* silent — download button still works */})
            .finally(() => setLoadingAadhaar(false));
    }, [downloadResult]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const msgs = { 500: 'Server error. Try again.', 422: 'Check your inputs.', 429: 'Too many requests.', 400: 'Invalid input.', 404: 'No record found.' };
        let detail = msgs[s] || error.response?.data?.message || error.message || summary;
        if (s === 402) {
            const c = error.response?.data || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    const setField = (name, transform) => (e) => {
        let v = e.target ? e.target.value : e.value;
        if (transform) v = transform(String(v));
        setForm((p) => ({ ...p, [name]: v }));
        if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    };

    const validateInit = () => {
        const next = {};
        if (!form.full_name.trim()) next.full_name = 'Full Name is required';
        if (!form.mobile_number) next.mobile_number = 'Mobile Number is required';
        else if (!/^[6-9]\d{9}$/.test(form.mobile_number)) next.mobile_number = 'Enter a valid 10-digit Indian mobile';
        if (!form.user_email.trim()) next.user_email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_email)) next.user_email = 'Enter a valid email';
        if (!form.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doInitialize = async () => {
        setLoading(true);
        try {
            const body = {
                data: {
                    prefill_options: {
                        full_name: form.full_name.trim(),
                        mobile_number: form.mobile_number,
                        user_email: form.user_email.trim(),
                    },
                    ...(form.expiry_minutes ? { expiry_minutes: parseInt(form.expiry_minutes, 10) } : {}),
                    ...(form.redirect_url ? { redirect_url: form.redirect_url.trim() } : {}),
                },
                api_provider_service_id: parseInt(serviceId, 10),
            };
            const res = await axiosInstance.post('/surepass/digilocker/initialize', body);
            if (!res.data.success) throw new Error(res.data.message || 'Initialization failed');
            const d = res.data?.data?.data || res.data?.data || {};
            setClientId(d.client_id || d.clientId || '');
            setDigilockerUrl(d.url || d.authorization_url || d.digilocker_url || '');
            toast.current?.show({ severity: 'success', summary: 'DigiLocker initialized', detail: 'Open the authorization URL to proceed', life: 3000 });
            setStep(1);
        } catch (err) {
            showError(err, 'Initialization failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleInitSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateInit();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Name:</span> <span className="text-900 font-medium">{form.full_name}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{form.mobile_number}</span></div>
                    <div className="mb-2"><span className="text-600">Email:</span> <span className="text-900 font-medium">{form.user_email}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm DigiLocker initialization',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, initialize',
            rejectLabel: 'Cancel',
            accept: doInitialize,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const doCheckStatus = async () => {
        setLoading(true);
        setStatusResult(null);
        try {
            const res = await axiosInstance.get('/surepass/digilocker/status', {
                params: { client_id: clientId, api_provider_service_id: parseInt(serviceId, 10) },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Status check failed');
            setStatusResult(res.data);
        } catch (err) {
            showError(err, 'Status check failed');
        } finally {
            setLoading(false);
        }
    };

    const doListDocuments = async () => {
        setLoading(true);
        setDocumentsResult(null);
        try {
            const res = await axiosInstance.get('/surepass/digilocker/list-documents', {
                params: { client_id: clientId, api_provider_service_id: parseInt(serviceId, 10) },
            });
            if (!res.data.success) throw new Error(res.data.message || 'List documents failed');
            setDocumentsResult(res.data);
        } catch (err) {
            showError(err, 'List documents failed');
        } finally {
            setLoading(false);
        }
    };

    const doDownload = async () => {
        setLoading(true);
        setDownloadResult(null);
        try {
            const res = await axiosInstance.get('/surepass/digilocker/download-document', {
                params: { client_id: clientId, file_id: fileId.trim(), api_provider_service_id: parseInt(serviceId, 10) },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Download failed');
            setDownloadResult(res.data);
        } catch (err) {
            showError(err, 'Download failed');
        } finally {
            setLoading(false);
        }
    };

    const resetFlow = () => {
        setStep(0);
        setClientId('');
        setDigilockerUrl('');
        setStatusResult(null);
        setDocumentsResult(null);
        setFileId('');
        setDownloadResult(null);
        setAadhaarData(null);
        setLoadingAadhaar(false);
        setForm({ full_name: '', mobile_number: '', user_email: '', expiry_minutes: '', redirect_url: '', consent: false });
        setErrors({});
    };

    const renderStep0 = () => (
        <Card>
            <form onSubmit={handleInitSubmit} noValidate>
                <div className="grid">
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="dlName" className="block font-medium text-sm mb-2">Full Name <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-user" />
                            <InputText id="dlName" value={form.full_name} onChange={setField('full_name')} placeholder="Enter full name" maxLength={100} disabled={loading}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.full_name })} />
                        </IconField>
                        {errors.full_name && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.full_name}</small>}
                    </div>
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="dlMobile" className="block font-medium text-sm mb-2">Mobile Number <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-phone" />
                            <InputText id="dlMobile" value={form.mobile_number} onChange={setField('mobile_number', (v) => v.replace(/\D/g, '').slice(0, 10))}
                                placeholder="9876543210" inputMode="tel" disabled={loading}
                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.mobile_number })} />
                        </IconField>
                        {errors.mobile_number && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.mobile_number}</small>}
                    </div>
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="dlEmail" className="block font-medium text-sm mb-2">Email <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-envelope" />
                            <InputText id="dlEmail" value={form.user_email} onChange={setField('user_email')} placeholder="user@example.com" type="email" disabled={loading}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.user_email })} />
                        </IconField>
                        {errors.user_email && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.user_email}</small>}
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="dlExpiry" className="block font-medium text-sm mb-2">Expiry (minutes)</label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-clock" />
                            <InputText id="dlExpiry" value={form.expiry_minutes} onChange={setField('expiry_minutes', (v) => v.replace(/\D/g, '').slice(0, 4))}
                                placeholder="30" inputMode="numeric" disabled={loading} className="w-full p-inputtext-sm font-mono" />
                        </IconField>
                    </div>
                    <div className="field col-12 md:col-8">
                        <label htmlFor="dlRedirect" className="block font-medium text-sm mb-2">Redirect URL</label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-link" />
                            <InputText id="dlRedirect" value={form.redirect_url} onChange={setField('redirect_url')} placeholder="https://your-app.com/callback" maxLength={300} disabled={loading}
                                className="w-full p-inputtext-sm" />
                        </IconField>
                    </div>
                </div>
                <ConsentCheckbox
                    checked={form.consent}
                    onChange={(e) => { setForm((p) => ({ ...p, consent: e.checked })); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                    error={errors.consent}
                    reportType="digilocker-initialize"
                />
                <div className="flex justify-content-end mt-3">
                    <Button type="submit" label="Initialize DigiLocker" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                </div>
            </form>
        </Card>
    );

    const renderStep1 = () => (
        <Card>
            <div className="text-center py-4">
                <i className="pi pi-folder-open text-6xl text-blue-500 block mb-3" />
                <h3 className="text-900 font-semibold m-0 mb-2">Authorize DigiLocker Access</h3>
                <p className="text-600 text-sm mb-4">Open the DigiLocker link below and complete authorization, then return here to continue.</p>
                {clientId && (
                    <div className="mb-4 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                        <i className="pi pi-key text-primary text-sm" />
                        <span className="text-600 text-xs">Session ID:</span>
                        <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
                    </div>
                )}
                <div className="flex flex-column align-items-center gap-3">
                    {digilockerUrl ? (
                        <Button label="Open DigiLocker Authorization" icon="pi pi-external-link" iconPos="right"
                            onClick={() => window.open(digilockerUrl, '_blank', 'noopener,noreferrer')} />
                    ) : (
                        <p className="text-orange-500 text-sm">Authorization URL not available in the response.</p>
                    )}
                    <div className="surface-50 border-round-lg border-1 surface-border p-3 text-sm text-600 max-w-25rem text-left">
                        <i className="pi pi-info-circle mr-2 text-primary" />
                        Log in to DigiLocker, grant consent, then come back and click the button below.
                    </div>
                    <Button
                        label="I've Completed Authorization — Continue"
                        icon="pi pi-check-circle"
                        severity="success"
                        onClick={() => { setStep(2); doCheckStatus(); }}
                    />
                </div>
            </div>
        </Card>
    );

    const renderStep2 = () => (
        <div>
            {loading && !statusResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Checking DigiLocker session status...</p>
                    </div>
                </Card>
            )}
            {statusResult && <GenericSurepassOutputComponent reportData={statusResult} title="DigiLocker Status" icon="pi-folder" />}
            {!loading && !statusResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Status check failed. Make sure you have completed DigiLocker authorization.</p>
                        <Button label="Retry Status Check" icon="pi pi-refresh" outlined onClick={doCheckStatus} />
                    </div>
                </Card>
            )}
            {statusResult && (
                <div className="flex justify-content-end mt-3">
                    <Button label="View Documents" icon="pi pi-list" iconPos="right" size="small"
                        onClick={() => { setStep(3); doListDocuments(); }} />
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div>
            {loading && !documentsResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Fetching DigiLocker documents...</p>
                    </div>
                </Card>
            )}
            {documentsResult && <GenericSurepassOutputComponent reportData={documentsResult} title="DigiLocker Documents" icon="pi-list" />}
            {!loading && !documentsResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Could not fetch documents. Click to retry.</p>
                        <Button label="Retry" icon="pi pi-refresh" outlined onClick={doListDocuments} />
                    </div>
                </Card>
            )}
            {documentsResult && (
                <Card className="mt-3">
                    <h4 className="text-900 font-semibold text-sm m-0 mb-3">
                        <i className="pi pi-download mr-2 text-primary" />
                        Download a Document
                    </h4>
                    <div className="field col-12 md:col-8 lg:col-6 p-0">
                        <label htmlFor="dlFileId" className="block font-medium text-sm mb-2">
                            File ID <span className="text-red-500">*</span>
                        </label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-file" />
                            <InputText id="dlFileId" value={fileId} onChange={(e) => setFileId(e.target.value)}
                                placeholder="Enter file_id from the document list above"
                                className="w-full p-inputtext-sm font-mono" />
                        </IconField>
                        <small className="text-500 block mt-1">Locate the file_id in the document list above and paste it here</small>
                    </div>
                    <div className="flex justify-content-end mt-3">
                        <Button label="Download Document" icon="pi pi-download" iconPos="right" size="small"
                            disabled={!fileId.trim()} onClick={() => { setStep(4); doDownload(); }} />
                    </div>
                </Card>
            )}
        </div>
    );

    const renderStep4 = () => {
        const d = downloadResult?.data?.data || downloadResult?.data || {};
        const downloadUrl = d.download_url || d.url || d.file_url || d.document_url ||
            Object.values(d).find((v) => typeof v === 'string' && v.startsWith('http'));
        const mimeType = d.mime_type || d.content_type || '';
        const isXml = mimeType.includes('xml');

        const handleDownload = async () => {
            if (!downloadUrl) return;
            try {
                // Fetch the file as a blob so the browser downloads it instead of
                // opening it — the `download` attribute is ignored for cross-origin
                // URLs (S3), but works fine on same-origin blob: URLs.
                const res = await fetch(downloadUrl);
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `aadhaar-kyc${isXml ? '.xml' : ''}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            } catch {
                // Fallback: open in tab if fetch fails
                window.open(downloadUrl, '_blank', 'noopener,noreferrer');
            }
        };

        return (
            <div>
                {loading && !downloadResult && (
                    <Card>
                        <div className="text-center py-4">
                            <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                            <p className="text-600 text-sm">Fetching document...</p>
                        </div>
                    </Card>
                )}
                {!loading && !downloadResult && (
                    <Card>
                        <div className="text-center py-3">
                            <p className="text-600 text-sm mb-3">Download failed. Verify the file_id and try again.</p>
                            <Button label="Retry" icon="pi pi-refresh" outlined onClick={doDownload} />
                        </div>
                    </Card>
                )}

                {/* Aadhaar card — shown when XML parsed successfully */}
                {downloadResult && loadingAadhaar && (
                    <Card className="mb-3">
                        <div className="text-center py-3">
                            <i className="pi pi-spin pi-spinner text-2xl text-primary block mb-2" />
                            <p className="text-600 text-sm">Generating Aadhaar card...</p>
                        </div>
                    </Card>
                )}

                {downloadResult && !loadingAadhaar && aadhaarData && (
                    <Card className="mb-3">
                        <div className="flex flex-column align-items-center gap-4">
                            <AadhaarCard data={aadhaarData} />
                            <Button
                                label="Download KYC Document"
                                icon="pi pi-download"
                                onClick={handleDownload}
                                disabled={!downloadUrl}
                                size="small"
                                outlined
                            />
                        </div>
                    </Card>
                )}

                {/* Fallback — no Aadhaar XML (other document types) */}
                {downloadResult && !loadingAadhaar && !aadhaarData && (
                    <Card className="mb-3">
                        <div className="text-center py-4">
                            <i className="pi pi-file text-5xl text-primary block mb-3" />
                            <p className="text-700 text-sm m-0 mb-3">Your DigiLocker document is ready.</p>
                            <Button
                                label="Download Document"
                                icon="pi pi-download"
                                onClick={handleDownload}
                                disabled={!downloadUrl}
                            />
                            {!downloadUrl && (
                                <p className="text-500 text-xs mt-2 m-0">Download link not available in response.</p>
                            )}
                        </div>
                    </Card>
                )}

                <div className="flex justify-content-end mt-3">
                    <Button label="Start New Session" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={resetFlow} />
                </div>
            </div>
        );
    };

    const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-folder-open"
                        title="DigiLocker Verification Flow"
                        subtitle="Complete DigiLocker document verification in one guided flow — no copy-pasting required"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default DigilockerFlowComponent;
